"use client";
import styles from "./Navbar.module.scss";
import { MenuType, PlatformType } from "@/types/baseTypes.ts";
import { OptionsHeaderType } from "@/types/OptionType";
import { NavigationOptionsType } from "@/types/ModuleOptionsType";
import { Logos } from "./Logos/Logos";
import { Menu } from "./Menu/Menu";
// import { Socials } from "./Socials/Socials";
// import { Search } from "./Search/Search";
// import { LanguageToggle } from "./Language/LanguageToggle";
import { Hamburger } from "./Hamburger/Hamburger";
import { useMotionValueEvent, useScroll, Variants } from "motion/react";
import { useRef, useState, KeyboardEvent } from "react";
import { useAppContext } from "../../context/AppContext";
import { FocusTrap } from "@aleksundshantu.com/aunds-utils-react/accessibility";
import { Post } from "@/wp/types/client";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { ReactSVG } from "react-svg";
import * as motion from "motion/react-m";
import { defaultEase } from "@/utils/animation/transitions";
import { Search } from "./Search/Search";
import { CustomLink } from "../Global/CustomLink";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import { useRouter } from "next/compat/router";
interface NavbarProps {
  menu: MenuType;
  searchPageSlug: string;
  socialMedia: PlatformType;
  navbarOption: NavbarOptionType;
  pageData: Post;
  isHome: boolean;
}

export type NavbarOptionType = {
  themeOption: OptionsHeaderType;
  moduleOption: NavigationOptionsType;
};

export const Navbar = ({
  navbarOption,
  menu,
  pageData,
  isHome,
  searchPageSlug,
}: NavbarProps) => {
  const { logos } = navbarOption.themeOption;
  const { show_box_shadow, show_background } =
    navbarOption.moduleOption.header_menu;

  const { getIcon, getLabel } = useGlobalContext();

  const [hasBackground, setHasBackground] = useState<boolean>(false);
  const { openMenu, user, logOut, showSearch } = useAppContext();
  const { scrollY } = useScroll();

  const [isLoginDropdownOpen, setIsLoginDropdownOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const router = useRouter();

  const closeMenuHandler = (
    e: KeyboardEvent<HTMLAnchorElement> | KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (e.key === "Escape") {
      setIsLoginDropdownOpen(false);
      buttonRef.current?.focus();
    }
  };

  useOnClickOutside(menuRef, () => setIsLoginDropdownOpen(false), "mousedown");

  useMotionValueEvent(scrollY, "change", (v) => {
    if (v > 100) {
      setHasBackground(true);
    } else {
      setHasBackground(false);
    }
  });

  return (
    <header>
      <nav
        data-has-bg={isHome && !show_background ? hasBackground : true}
        data-show-shadow={show_box_shadow}
        //! please dont remove scrollbar-width class
        className={`${styles["navbar"]} ${openMenu ? styles["opened"] : ""} navbar scrollbar-width`}
        aria-label="Hauptnavigation der Webseite"
      >
        <div className={`${styles["level-1-wrapper"]} section-padding-x`}>
          <div className={`container ${styles["level-1"]} `}>
            <div className={styles["left"]}>
              <Hamburger />
              <Logos logos={logos} show="first" />
            </div>

            <div className={styles["right"]}>
              <Logos logos={logos} show="second" />

              {isHome ? (
                <motion.div
                  key={"animtion-search"}
                  variants={width_variant}
                  initial={"initial"}
                  animate={showSearch ? "animate" : "exit"}
                  exit={"exit"}
                  className={styles["search-desktop"]}
                >
                  <Search
                    searchPageSlug={searchPageSlug}
                    isFocussable={showSearch}
                  />
                </motion.div>
              ) : (
                <div
                  key={"no-animtion-search"}
                  className={styles["search-desktop"]}
                >
                  <Search searchPageSlug={searchPageSlug} />
                </div>
              )}

              <div className={styles["profile-pages-btns"]}>
                {user && (
                  <>
                    <CustomLink
                      href={`/meine-favoriten`}
                      target="_self"
                      style={{ zIndex: 2 }}
                      aria-label={getLabel("favorites_link_aria_label")}
                    >
                      <ReactSVG
                        src={getIcon("heartEmpty")}
                        wrapper={"svg"}
                        width={32}
                        height={32}
                        aria-hidden
                        beforeInjection={(svg) => {
                          svg.setAttribute("aria-label", "Herz Icon");
                          svg.setAttribute("alt", "Herz Icon");
                        }}
                      />
                    </CustomLink>

                    <CustomLink
                      href={`/einkaufsliste`}
                      target="_self"
                      style={{ zIndex: 2 }}
                      aria-label={getLabel("shoppinglist_link_aria_label")}
                    >
                      <ReactSVG
                        src={getIcon("shoppingList")}
                        wrapper={"svg"}
                        width={32}
                        height={32}
                        aria-hidden
                        beforeInjection={(svg) => {
                          svg.setAttribute("aria-label", "Einkaufsliste Icon");
                          svg.setAttribute("alt", "Einkaufsliste Icon");
                        }}
                      />
                    </CustomLink>
                  </>
                )}

                <div className={styles["profile-btns"]} ref={menuRef}>
                  <button
                    ref={buttonRef}
                    className={styles["profile-btn"]}
                    data-user={user ? "true" : "false"}
                    aria-haspopup="menu"
                    aria-expanded={isLoginDropdownOpen}
                    aria-controls="login-dropdown-menu"
                    aria-label="Menu öffnen und schließen"
                    onMouseEnter={() => setIsLoginDropdownOpen((prev) => !prev)}
                    onTouchStart={() => setIsLoginDropdownOpen((prev) => !prev)}
                    onClick={() => {
                      router?.push("/profile");
                      setIsLoginDropdownOpen((prev) => !prev);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setIsLoginDropdownOpen(true);
                      }
                    }}
                  >
                    {user && (
                      <span>
                        {user.firstName.charAt(0)} {user.lastName.charAt(0)}
                      </span>
                    )}

                    {!user && (
                      <ReactSVG
                        src={getIcon("login")}
                        wrapper={"svg"}
                        width={32}
                        height={32}
                        aria-hidden
                      />
                    )}
                  </button>

                  <menu
                    id="login-dropdown-menu"
                    className={`${styles["login-dropdown"]} is-hidden-touch`}
                    data-is-open={isLoginDropdownOpen}
                  >
                    <div className={styles.triangle} aria-hidden />

                    <FocusTrap
                      dep={isLoginDropdownOpen}
                      disabled={!isLoginDropdownOpen}
                      autoFocus
                    >
                      <ul className={styles["login-dropdown-container"]}>
                        <li>
                          <CustomLink
                            href={"/profile"}
                            target="_self"
                            style={{ zIndex: "10" }}
                            tabIndex={isLoginDropdownOpen ? 0 : -1}
                            onKeyDown={closeMenuHandler}
                            aria-label={getLabel(
                              "profile_page_link_aria_label",
                              "link to your profile page",
                            )}
                          >
                            {getLabel("my_profile_label")}
                          </CustomLink>
                        </li>

                        {user && (
                          <li>
                            <button
                              onClick={() => logOut()}
                              tabIndex={isLoginDropdownOpen ? 0 : -1}
                              onKeyDown={closeMenuHandler}
                              aria-label={getLabel("logout_aria_label")}
                            >
                              {getLabel("logout_label", "Login")}
                            </button>
                          </li>
                        )}

                        {!user && (
                          <li>
                            <CustomLink
                              href={"/login"}
                              target="_self"
                              tabIndex={isLoginDropdownOpen ? 0 : -1}
                              onKeyDown={closeMenuHandler}
                              aria-label={getLabel("login_aria_label")}
                            >
                              {getLabel("login_label", "Login")}
                            </CustomLink>
                          </li>
                        )}
                      </ul>
                    </FocusTrap>
                  </menu>
                </div>
              </div>
            </div>
          </div>

          <div className={`container ${styles["search-touch"]}`}>
            <Search searchPageSlug={searchPageSlug} />
          </div>
        </div>

        <div className={`${styles["level-2-wrapper"]}`}>
          <FocusTrap autoFocus dep={openMenu} disabled={!openMenu}>
            <div
              data-level="2"
              data-is-open={openMenu}
              data-disabled={!openMenu}
              className={`${styles["level-2"]}`}
            >
              <Menu menu={menu} pageData={pageData} />

              {openMenu && (
                <>
                  <div className={styles["footer-menu-logo"]}>
                    <Logos logos={logos} show="second" />
                  </div>

                  <div className={styles["btn-wrapper"]}>
                    {user ? (
                      <button
                        className={styles["login-button"]}
                        onClick={() => logOut()}
                        aria-label={getLabel("logout_aria_label")}
                      >
                        <ReactSVG
                          src={getIcon("login")}
                          wrapper={"svg"}
                          width={32}
                          height={32}
                          aria-hidden
                        />
                        {getLabel("logout_label", "Logout")}
                      </button>
                    ) : (
                      <CustomLink
                        href={"/login"}
                        target="_self"
                        className={styles["login-button"]}
                        aria-label={getLabel("login_aria_label")}
                      >
                        <ReactSVG
                          src={getIcon("login")}
                          wrapper={"svg"}
                          width={32}
                          height={32}
                          aria-hidden
                        />
                        {getLabel("login_label", "Login")}
                      </CustomLink>
                    )}
                  </div>
                </>
              )}
            </div>
          </FocusTrap>
        </div>
      </nav>
    </header>
  );
};

export const width_variant: Variants = {
  initial: {
    width: 0,
    opacity: 0,
  },
  animate: {
    width: "auto",
    opacity: 1,
    transition: {
      width: { duration: 1, ease: defaultEase },
      opacity: { delay: 0.5, duration: 1, ease: defaultEase },
    },
  },
  exit: {
    width: 0,
    opacity: 0,
    transition: {
      width: { duration: 1, ease: defaultEase },
      opacity: { duration: 0.5, ease: defaultEase },
    },
  },
};
